import { sitesApi, ApiError, subscriptionsApi, blogApi, layoutsApi } from '../../../api/client';
import { markOpenedSite } from '../../../components/GettingStarted';
import type { Page } from '../../../schemas/validation';
import type { SiteManagerState } from '../useSiteManagerState';

interface DataLoaderDependencies {
  siteId: string | undefined;
  navigate: (path: string) => void;
  state: SiteManagerState;
}

export const createDataLoaders = ({ siteId, navigate, state }: DataLoaderDependencies) => {
  const {
    setSite,
    setPages,
    setIsLoading,
    setCanCreatePage,
    setBlogData,
    setIsBlogLoading,
    setLayouts,
    setIsLayoutsLoading,
  } = state;

  const loadBlogData = async () => {
    if (!siteId) return;

    try {
      setIsBlogLoading(true);
      const data = await blogApi.get(siteId);
      setBlogData(data);
    } catch (error: unknown) {
      const apiError = error as { status?: number };
      if (apiError?.status !== 404) {
        console.error('Failed to load blog data:', error);
      }
      setBlogData(null);
    } finally {
      setIsBlogLoading(false);
    }
  };

  const loadLayoutsData = async () => {
    if (!siteId) return;

    try {
      setIsLayoutsLoading(true);
      const data = await layoutsApi.list(siteId);
      setLayouts(data.layouts);
    } catch (error) {
      console.error('Failed to load layouts:', error);
      setLayouts([]);
    } finally {
      setIsLayoutsLoading(false);
    }
  };

  const checkPageCreationLimit = async () => {
    try {
      const { canCreate } = await subscriptionsApi.canCreatePage(siteId!);
      setCanCreatePage(canCreate);
    } catch (error) {
      console.warn('Failed to check page creation limit, allowing creation:', error);
      setCanCreatePage(true);
    }
  };

  const loadSiteData = async () => {
    try {
      setIsLoading(true);
      const allSites = await sitesApi.list();
      const currentSite = allSites.find(s => s.id === siteId);

      if (!currentSite) {
        console.error('Site not found in user sites');
        navigate('/dashboard');
        return;
      }

      setSite(currentSite);
      markOpenedSite();

      try {
        const pagesData = await sitesApi.getPages(siteId!);
        const sortedPages = pagesData
          .map((page, index) => ({
            ...page,
            orderPosition: page.orderPosition ?? index,
          }))
          .sort((a, b) => (a.orderPosition || 0) - (b.orderPosition || 0));

        setPages(sortedPages);
        try {
          localStorage.setItem(`pages-${siteId}`, JSON.stringify(sortedPages));
        } catch {
          // localStorage quota exceeded — non-fatal, pages are already set in state
        }
      } catch (pageError) {
        console.error('Failed to load pages from API:', pageError);
        try {
          const fallbackPages = JSON.parse(localStorage.getItem(`pages-${siteId}`) || '[]');
          if (fallbackPages.length > 0) {
            const sortedFallbackPages = fallbackPages
              .map((page: Page, index: number) => ({
                ...page,
                orderPosition: page.orderPosition ?? index,
              }))
              .sort((a: Page, b: Page) => (a.orderPosition || 0) - (b.orderPosition || 0));
            setPages(sortedFallbackPages);
          } else {
            setPages([]);
          }
        } catch (localError) {
          console.error('Failed to load fallback pages:', localError);
          setPages([]);
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate('/login');
      } else {
        console.error('Failed to load site data:', error);
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loadSiteData,
    loadBlogData,
    loadLayoutsData,
    checkPageCreationLimit,
  };
};
